import { Collapse, ConfigProvider, Drawer, message, Pagination, Table, theme, Tooltip } from 'antd';
import React, { useEffect, useState } from 'react';
import { MakeApiCall } from '../../../../../apis';
import { ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import { Link } from 'react-router-dom';
import {getAmount} from "../../../../../utils/helper";

const { Panel } = Collapse;

const OrderItems = ({ userData, selectedMarketplace, selectedCurrencySign, selectedTileData }) => {
    const [pageSize, setPageSize] = useState(defaultPageSize);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [list, setList] = useState([]);
    const [totalPage, setTotalPage] = useState(0);

    const [selectedRecord, setSelectedRecord] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    const [filter, setFilter] = useState({
        page: 1,
        "per-page": defaultPageSize,
        marketplace_id: selectedMarketplace?.id,
        start_date: selectedTileData?.start_date,
        end_date: selectedTileData?.end_date,
    });

    const getProductList = async (currentFilter) => {
        // Prevent API call if essential data is missing (e.g., marketplace not selected)
        if (!currentFilter?.marketplace_id) {
            setList([]);
            setTotalPage(0);
            return;
        }

        setLoading(true);
        try {
            const response = await MakeApiCall(
                `dashboard/tiles/get-ordered-product-analytics-data${ConvertParams(currentFilter)}`,
                "get",
                null,
                true,
                {},
                userData?.token
            );

            if (response?.status) {
                const pagination = response?.data?.pagination || {};
                setTotalPage(pagination.totalCount || 0);

                const recordsObject = response?.data?.records || {};
                const recordsArray = Object.values(recordsObject);

                setList(recordsArray);
                // Sync state with response (important if API can override page/size)
                setPage(pagination.page || currentFilter.page);
                setPageSize(pagination.page_size || currentFilter["per-page"]);
            } else {
                message.warning(response?.message);
                setList([]);
            }
        } catch (error) {
            message.error("Something went wrong while fetching data");
            console.error(error);
            setList([]);
        } finally {
            setLoading(false);
        }
    };

    // ✅ FIX: Use a single useEffect to handle changes in props and local filter
    useEffect(() => {
        // 1. Update filter state if marketplace or date range props change
        const currentMarketplaceId = selectedMarketplace?.id;
        const currentStartDate = selectedTileData?.start_date;
        const currentEndDate = selectedTileData?.end_date;

        if (
            filter.marketplace_id !== currentMarketplaceId ||
            filter.start_date !== currentStartDate ||
            filter.end_date !== currentEndDate
        ) {
            // Reset to page 1 when main filters (marketplace/dates) change
            setFilter(prev => ({
                ...prev,
                page: 1,
                marketplace_id: currentMarketplaceId,
                start_date: currentStartDate,
                end_date: currentEndDate,
            }));
        } else {
            // 2. Call API if the filter is stable (including pagination changes handled by onChange)
            getProductList(filter);
        }

        // Dependencies include filter to track pagination changes, and props to track external changes
    }, [filter, selectedMarketplace?.id, selectedTileData?.start_date, selectedTileData?.end_date]);


    const openDrawer = (record) => {
        setSelectedRecord(record);
        setDrawerOpen(true);
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedRecord(null);
    };

    const columns = [
        {
            title: "Order number",
            dataIndex: "order_id",
            key: "order_id",
            render: (text, record) => (
                <div className="d-flex flex-column">
                    <Tooltip
                        title={
                            <>
                                <div><b>Order ID:</b> {record.order_id}</div>
                                <div><b>Date:</b> {new Date(record.purchase_date).toLocaleString()}</div>
                                <div><b>Status:</b> {record.order_status}</div>
                                <div><b>Channel:</b> {record?.sale_channel}</div>
                                <div><b>COGS:</b> {selectedCurrencySign}{record.product_cogs?.toFixed(2) || 0}</div>
                            </>
                        }
                    >
                <span
                    className="fw-bold text-dark"
                    style={{
                        maxWidth: "180px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "inline-block",
                        cursor: "pointer",
                    }}
                >
                    {record.order_id}
                </span>
                    </Tooltip>
                    <div className="fs-7 text-gray-800">
                        {new Date(record.purchase_date).toLocaleString()} • {record.order_status}
                    </div>
                    <div className="fs-7 text-gray-800">
                        {record?.sale_channel} / COGS: <b>{selectedCurrencySign}{record.product_cogs?.toFixed(2) || 0}</b>
                    </div>
                </div>
            ),
        },
        {
            title: "Order Details",
            key: "userDetails",
            width: 450,
            render: (_, record) => (
                <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                    <div className="symbol symbol-65px overflow-hidden me-3">
                        <img src={record?.image_url || fakeProImage} alt="" />
                    </div>
                    <div className="d-flex flex-column">
                        <Tooltip title={record?.product_name || ''}>
                    <span
                        className="text-gray-800 fs-6 fw-bolder"
                        style={{
                            maxWidth: "300px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "inline-block",
                            cursor: "pointer",
                        }}
                    >
                        {record?.product_name || ''}
                    </span>
                        </Tooltip>

                        <div className="fs-7 mt-1">
                            <b>SKU:</b> {record?.sku} / <b>ASIN:</b> {record?.asin}
                        </div>
                        <div className="fs-7 mt-1">
                            Price: <b>{selectedCurrencySign}{record?.price}</b>
                        </div>
                    </div>
                </div>
            ),
        },
        { title: "Units Sold", dataIndex: "unit_sold", key: "unit_sold" },
        { title: "Refund Units", dataIndex: "refund_unit", key: "refund_unit" },
        { title: "Sales", dataIndex: "sales", key: "sales", render: (text) => getAmount(text, selectedCurrencySign)},
        { title: "Amazon Fees", dataIndex: "amazon_fees", key: "amazon_fees", render: (text) => getAmount(text, selectedCurrencySign) },
        { title: "Gross Profit", dataIndex: "gross_profit", key: "gross_profit", render: (text) => getAmount(text, selectedCurrencySign) },
        {
            title: "Info",
            key: "info",
            render: (_, record) => <Link onClick={() => openDrawer(record)} className="text-primary">More</Link>,
        },
    ];

    const generateCollapseItems = (selectedRecord) => {
        if (!selectedRecord) return [];
        const latestData = selectedRecord;

        // Helper function for safe toFixed(2)
        const formatCurrency = (value) => `${selectedCurrencySign}${(typeof value === 'number' ? value : 0).toFixed(2)}`;

        return [
            {
                key: '1',
                title: 'Sales',
                amount: formatCurrency(latestData?.more_info?.sales),
            },
            {
                key: '2',
                title: 'Units',
                amount: `${latestData?.more_info?.units || 0}`,
            },
            {
                key: '3',
                title: 'Promo',
                amount: formatCurrency(latestData?.more_info?.promo),
            },
            {
                key: '4',
                title: 'Gift Wrap',
                amount: formatCurrency(latestData?.more_info?.giftwrap),
            },
            {
                key: '5',
                title: 'Shipping Cost',
                amount: formatCurrency(latestData?.more_info?.shipping_cost),
            },
            {
                key: '6',
                title: 'Refund Cost',
                amount: formatCurrency(latestData?.more_info?.refund_cost),
            },
            {
                key: '7',
                title: 'Amazon Fees',
                amount: formatCurrency(latestData?.more_info?.amazon_fees?.total),
                items: [
                    { label: 'FBA Fulfilment Fee', value: formatCurrency(latestData?.more_info?.amazon_fees?.fba_per_unit_fulfilment_fee) },
                    { label: 'Referral Fee', value: formatCurrency(latestData?.more_info?.amazon_fees?.referral_fee) },
                ]
            },
            {
                key: '8',
                title: 'Cost of Goods',
                amount: formatCurrency(latestData?.more_info?.cost_of_goods),
            },
            {
                key: '9',
                title: 'Vat',
                amount: formatCurrency(latestData?.more_info?.vat),
            },
            {
                key: '10',
                title: 'Gross Profit',
                amount: formatCurrency(latestData?.more_info?.gross_profit),
            },
            {
                key: '11',
                title: 'Net Profit',
                amount: formatCurrency(latestData?.more_info?.net_profit),
            },
            {
                key: '12',
                title: 'Estimated Payout',
                amount: formatCurrency(latestData?.more_info?.estimated_payout),
            },
            {
                key: '13',
                title: 'Refund %',
                amount: `${latestData?.more_info?.refunds_percent || 0}%`,
            },
            {
                key: '14',
                title: 'Sellable Returns',
                amount: `${latestData?.more_info?.sellable_returns || 0}%`,
            },
            {
                key: '15',
                title: 'Margin',
                amount: `${latestData?.more_info?.margin || 0}%`,
            },
            {
                key: '16',
                title: 'ROI',
                amount: `${latestData?.more_info?.roi || 0}%`,
            },
        ].map(section => {
            const hasChildren = Boolean(section.items && section.items.length > 0);

            return {
                key: section.key,
                label: (
                    <div className="d-flex justify-content-between w-100">
                        <span className="text-gray-800 fs-6">{section.title}</span>
                        <span className="text-gray-800 fw-bolder fs-6 me-3">{section.amount}</span>
                    </div>
                ),
                children: hasChildren ? (
                    <div className='p-3'>
                        {section.items.map((item, index) => (
                            <div key={index} className="d-flex justify-content-between mb-1">
                                <span className="text-gray-600">{item.label}</span>
                                <span className="text-gray-800 fw-medium">{item.value}</span>
                            </div>
                        ))}
                    </div>
                ) : null,
                showArrow: hasChildren,
                collapsible: hasChildren ? undefined : 'disabled',
            };
        });
    };

    const collapseItems = generateCollapseItems(selectedRecord);

    return (
        <div>
            <div className="table-responsive">
                <Table
                    columns={columns}
                    loading={loading}
                    dataSource={Array.isArray(list) ? list : []}
                    rowKey={(record) => record.order_id}
                    pagination={false}
                />
            </div>

            <ConfigProvider theme={theme}>
                <Pagination
                    className="mt-3"
                    current={page}
                    pageSize={pageSize}
                    total={totalPage}
                    showSizeChanger
                    onChange={(currentPage, newPageSize) => {
                        // Keep local states in sync
                        setPage(currentPage);
                        setPageSize(newPageSize);

                        // Update filter to trigger API call
                        setFilter(prev => ({
                            ...prev,
                            page: currentPage,
                            "per-page": newPageSize, // request key
                        }));
                    }}
                    showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} items`}
                />
            </ConfigProvider>

            <Drawer
                title="More Info Details"
                placement="right"
                onClose={closeDrawer}
                open={drawerOpen}
                width={400}
            >
                {selectedRecord?.more_info ? (
                    <Collapse
                        bordered={false}
                        className="custom-collapse"
                        accordion
                        items={collapseItems}
                        expandIcon={({ isActive }) => (
                            <div className="">
                                {isActive ? (
                                    <i className="ki-outline ki-down fs-3 text-gray-600"></i>
                                ) : (
                                    <i className="ki-outline ki-right fs-3 text-gray-600"></i>
                                )}
                            </div>
                        )}
                    />
                ) : (
                    <p>No additional info available.</p>
                )}
            </Drawer>
        </div>
    );
};

export default OrderItems;